import type { DotLottie } from '@lottiefiles/dotlottie-react'
import { DotLottieReact } from '@lottiefiles/dotlottie-react'
import { useQueryClient } from '@tanstack/react-query'
import { useEffect, useRef, useState } from 'react'

import { queries } from 'src/lib/query-keys'
import { sendEvent } from 'src/shared/api/events/send-event'
import onboardingComplete from 'src/shared/assets/lottie/onboarding-complete.lottie'
import { Button } from 'src/shared/components/ui/buttons/button'
import { useEventStore } from 'src/shared/store/event-store'

const CompletionStep = () => {
  const queryClient = useQueryClient()
  const hasInitializedRef = useRef(false)
  const [lottieRef, setLottieRef] = useState<DotLottie | null>(null)
  // Access only what we need from event store
  const anonymousId = useEventStore((state) => state.anonymousId)
  const sessionId = useEventStore((state) => state.sessionId)
  const flowId = useEventStore((state) => state.flowId)
  const setAnonymousId = useEventStore((state) => state.setAnonymousId)
  const setSessionId = useEventStore((state) => state.setSessionId)
  const setFlowId = useEventStore((state) => state.setFlowId)

  useEffect(() => {
    if (lottieRef) {
      lottieRef.play()
    }
  }, [lottieRef])

  // Setup event IDs at component mount time, only once
  useEffect(() => {
    if (hasInitializedRef.current) return

    if (!anonymousId) {
      setAnonymousId()
    }
    if (!sessionId) {
      setSessionId()
    }
    if (!flowId) {
      setFlowId()
    }

    hasInitializedRef.current = true
  }, [anonymousId, sessionId, flowId, setAnonymousId, setSessionId, setFlowId])

  const handleClick = async () => {
    await queryClient.invalidateQueries({ queryKey: queries.auth.session.queryKey })

    // Use the values from the hooks instead of directly accessing the store
    if (anonymousId && sessionId && flowId) {
      sendEvent({
        anonymousId: anonymousId,
        eventType: 'flow_end',
        info: {
          flowType: 'onboarding',
          flowStep: 'confirm_user_registration',
          flowId: flowId,
          sessionId: sessionId,
        },
      })
    }
  }

  return (
    <div className="flex flex-col items-center space-y-6 py-8">
      <div className="relative size-[120px]">
        <DotLottieReact dotLottieRefCallback={setLottieRef} src={onboardingComplete} autoplay speed={1} />
      </div>
      <div className="text-center">
        <h3 className="text-xl font-semibold">Congratulations!</h3>
        <p className="mt-2 text-muted">You are now live on Conrad!</p>
      </div>
      <Button variant="default" onClick={handleClick} className="w-full">
        Join Network!
      </Button>
    </div>
  )
}

CompletionStep.displayName = 'CompletionStep'

export default CompletionStep
